import localForage from 'localforage';

// Default keeps localForage happy when the env var isn't set in local dev —
// it requires a string key and emits "undefined used as a key" warnings
// otherwise.
const STORAGE_DB_NAME = process.env.STORAGE_DB_NAME || 'zoe_portal_storage';

async function persistLocally(data: unknown): Promise<void> {
  try {
    await localForage.setItem(STORAGE_DB_NAME, JSON.stringify(data));
  } catch {
    // Do nothing
  }
}

// `typeof` alone can't narrow a generic `T | ((prev: T) => T)` union (T might
// itself be a function type), so an explicit predicate is needed here instead
// of `instanceof Function`.
function isUpdaterFunction<T>(
  value: T | ((prevData: T) => T),
): value is (prevData: T) => T {
  return typeof value === 'function';
}

/**
 * Allows to individually create multiple instances of the same `localStorage`
 * entity providing a single source of truth for data management also the ability to create instances of IndexedDB
 *
 * @param storageName string
 * @param emptyState [Default value for an empty state]
 *
 * Usage:
 * `const storage = new DynamicStorage<string[]>('array', []);`
 *
 */
export default class DynamicStorage<T> {
  storageName: string;

  emptyState: T;

  private localData: Record<string, T>;

  constructor(storageName: string, emptyState: T) {
    this.storageName = storageName;
    this.emptyState = emptyState;
  }

  private async getLocalData(): Promise<void> {
    try {
      const local = await localForage.getItem<string>(STORAGE_DB_NAME);

      this.localData = local ? JSON.parse(local) : {};
    } catch {
      this.localData = {};
    }
  }

  async get(): Promise<T> {
    await this.getLocalData();

    return this.localData?.[this.storageName] || this.emptyState;
  }

  /**
   * Standard usage: storage.set(...);
   *
   * Advanced usage (similar to `useState` hook):
   *  storage.set((prevStorage) => {
   *    return ...;
   *  });
   */
  async set(data: T | ((prevData: T) => T)): Promise<T> {
    await this.getLocalData();

    if (isUpdaterFunction(data)) {
      const newStorageData = data(this.localData[this.storageName]);

      this.localData = {
        ...this.localData,
        [this.storageName]: newStorageData,
      };
    } else if (data) {
      this.localData = {
        ...this.localData,
        [this.storageName]: data,
      };

      await persistLocally(this.localData);
    }

    await this.getLocalData();

    return this.localData[this.storageName];
  }

  async delete(): Promise<void> {
    await this.getLocalData();

    this.localData[this.storageName] = this.emptyState;

    await persistLocally(this.localData);
  }
}

/**
 * Method to globally clear ALL data handled by `Storage` instances
 * Eg. user log out
 */
export async function clearStorage() {
  try {
    await localForage.removeItem(STORAGE_DB_NAME);
  } catch {
    // Do nothing
  }
}
